/**
 * Conversations table with data fetching, pagination, and search.
 * External dependencies are injected via props.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
  type ColumnDef,
  type PaginationState,
  type SortingState,
} from '@tanstack/react-table';
import {
  AlertCircleIcon,
  CalendarClockIcon,
  ClipboardCopy,
  CheckCircle2Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
  GlobeIcon,
  HashIcon,
  LinkIcon,
  LoaderIcon,
  LockIcon,
  MessageSquareIcon,
  MoreHorizontalIcon,
  PauseCircleIcon,
  PencilIcon,
  SearchIcon,
  TrashIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/client/components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '@/client/components/ui/avatar';
import { Button } from '@/client/components/ui/button';
import { Skeleton } from '@/client/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/client/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/client/components/ui/select';
import { Input } from '@/client/components/ui/input';
import {
  deleteThread,
  listThreadsWithDates,
  resolveThreadTitle,
  updateThreadMetadata,
  type ListThreadsOptions,
  type ThreadStatus,
  type ThreadWithDates,
} from '@/client/lib/chatApi';
import { formatRelativeDate } from '@/client/lib/dateUtils';

const STATUS_CONFIG: Record<
  ThreadStatus,
  { label: string; icon: typeof CheckCircle2Icon; className: string }
> = {
  idle: { label: 'Completed', icon: CheckCircle2Icon, className: 'text-muted-foreground' },
  busy: { label: 'Running', icon: LoaderIcon, className: 'text-blue-500' },
  interrupted: { label: 'Interrupted', icon: PauseCircleIcon, className: 'text-amber-500' },
  error: { label: 'Error', icon: AlertCircleIcon, className: 'text-destructive' },
};

type MetadataFilters = {
  source?: string;
  visibility?: string;
  user_name?: string;
};

export interface ConversationsTableProps {
  /** Called when the user clicks a table row. */
  onRowClick: (threadId: string) => void;
  /** Whether the current user has admin privileges. */
  isAdmin: boolean;
  /** Current user's email (for permission checks). */
  currentUserEmail: string;
  /** Current user's display name (for "me" label in user filter). */
  currentUserName?: string;
  /** URL path prefix for copy-link generation. Omit to hide the copy-link action. */
  pathPrefix?: string;
  /** Called after a successful delete (e.g. to refresh sidebar). */
  onDeleteSideEffect?: () => void;
  /** Called when the user clicks "Copy Thread ID" for a thread. */
  onCopyThreadId?: (threadId: string) => void;
  /** When true, hides Source + Created columns and the copy-link action. */
  compact?: boolean;
}

export function ConversationsTable({
  onRowClick,
  isAdmin,
  currentUserEmail,
  currentUserName,
  pathPrefix,
  onCopyThreadId,
  onDeleteSideEffect,
  compact,
}: ConversationsTableProps) {
  const [threads, setThreads] = useState<ThreadWithDates[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: compact ? 20 : 10,
  });
  const [sorting, setSorting] = useState<SortingState>([{ id: 'updated_at', desc: true }]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [metadataFilters, setMetadataFilters] = useState<MetadataFilters>({});
  const [statusFilter, setStatusFilter] = useState<ThreadStatus | undefined>();
  const [userNames, setUserNames] = useState<string[]>([]);
  const [renamingThreadId, setRenamingThreadId] = useState<string | null>(null);

  const handleRename = useCallback((threadId: string) => {
    setRenamingThreadId(threadId);
  }, []);

  const handleRenameSave = useCallback(async (threadId: string, newTitle: string) => {
    setRenamingThreadId(null);
    const trimmed = newTitle.trim();
    if (!trimmed) return;
    await updateThreadMetadata(threadId, { conversation_title: trimmed });
    setThreads((prev) =>
      prev.map((t) => (t.thread_id === threadId ? { ...t, title: trimmed } : t)),
    );
    toast.success('Conversation renamed');
  }, []);

  // Build API options from current table state
  const fetchOptions = useMemo((): ListThreadsOptions => {
    const sort = sorting[0];
    const sortBy = (sort?.id === 'created_at' ? 'created_at' : 'updated_at') as
      | 'created_at'
      | 'updated_at';
    const sortOrder = sort?.desc === false ? 'asc' : 'desc';

    const metadata: Record<string, unknown> = {};
    if (metadataFilters.source) metadata.source = metadataFilters.source;
    if (metadataFilters.visibility) metadata.visibility = metadataFilters.visibility;
    if (metadataFilters.user_name) metadata.user_name = metadataFilters.user_name;

    return {
      limit: pagination.pageSize,
      offset: pagination.pageIndex * pagination.pageSize,
      sortBy,
      sortOrder,
      status: statusFilter,
      metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
    };
  }, [pagination, sorting, metadataFilters, statusFilter]);

  // Fetch threads when pagination, sorting, or filters change
  useEffect(() => {
    let cancelled = false;
    setIsFetching(true);

    listThreadsWithDates(fetchOptions)
      .then(({ threads: data, totalCount: count }) => {
        if (cancelled) return;
        setThreads(data);
        setTotalCount(count);
        setIsLoading(false);
        setIsFetching(false);

        // Progressively resolve titles for threads still showing "New Chat"
        const untitled = data.filter((t) => t.title === 'New Chat');
        for (const t of untitled) {
          resolveThreadTitle(t.thread_id).then((title) => {
            if (cancelled || !title) return;
            setThreads((prev) =>
              prev.map((row) => (row.thread_id === t.thread_id ? { ...row, title } : row)),
            );
          });
        }

        // Collect unique user names for the dropdown
        const names = Array.from(
          new Set(data.map((t) => t.user_name).filter(Boolean) as string[]),
        );
        setUserNames((prev) => {
          const merged = Array.from(new Set([...prev, ...names]));
          merged.sort((a, b) => a.localeCompare(b));
          return merged;
        });
      })
      .catch(() => {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetching(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [fetchOptions]);

  // Sort user names to put current user first
  const sortedUserNames = useMemo(() => {
    if (currentUserName && userNames.includes(currentUserName)) {
      return [currentUserName, ...userNames.filter((n) => n !== currentUserName)];
    }
    return userNames;
  }, [userNames, currentUserName]);

  const handleDelete = useCallback(
    async (threadId: string) => {
      await deleteThread(threadId);
      onDeleteSideEffect?.();
      setThreads((prev) => prev.filter((t) => t.thread_id !== threadId));
      setTotalCount((prev) => Math.max(0, prev - 1));
      toast.success('Conversation deleted');
    },
    [onDeleteSideEffect],
  );

  const handleToggleVisibility = useCallback(
    async (threadId: string, currentVisibility: string | undefined) => {
      const newVisibility = currentVisibility === 'public' ? 'private' : 'public';
      await updateThreadMetadata(threadId, { visibility: newVisibility });
      setThreads((prev) =>
        prev.map((t) =>
          t.thread_id === threadId ? { ...t, visibility: newVisibility } : t,
        ),
      );
      toast.success(`Conversation is now ${newVisibility}`);
    },
    [],
  );

  const handleMetadataFilterChange = useCallback(
    (key: keyof MetadataFilters, value: string) => {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      setMetadataFilters((prev) => ({
        ...prev,
        [key]: value === 'all' ? undefined : value,
      }));
    },
    [],
  );

  const pageCount = Math.ceil(totalCount / pagination.pageSize);

  const columns = useMemo<ColumnDef<ThreadWithDates>[]>(() => {
    const cols: ColumnDef<ThreadWithDates>[] = [
      {
        accessorKey: 'title',
        header: 'Title',
        enableSorting: false,
        cell: ({ row }) =>
          renamingThreadId === row.original.thread_id ? (
            <InlineRenameInput
              defaultValue={row.original.title}
              onSave={(v) => void handleRenameSave(row.original.thread_id, v)}
              onCancel={() => setRenamingThreadId(null)}
            />
          ) : (
            <span className="font-medium">{row.original.title}</span>
          ),
        enableHiding: false,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => {
          const status = row.original.status;
          const config = STATUS_CONFIG[status];
          const Icon = config.icon;
          return (
            <span className={`flex items-center gap-1.5 text-sm ${config.className}`}>
              <Icon
                className={`size-3.5 shrink-0${status === 'busy' ? ' animate-spin' : ''}`}
              />
              {config.label}
            </span>
          );
        },
      },
      {
        accessorKey: 'user_name',
        header: 'User',
        enableSorting: false,
        cell: ({ row }) => {
          const { user_name, user_picture } = row.original;
          if (!user_name) {
            return <span className="text-muted-foreground">&mdash;</span>;
          }
          const initials = user_name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .slice(0, 2)
            .toUpperCase();
          return (
            <div className="flex items-center gap-2">
              <Avatar className="h-6 w-6 text-[10px]">
                {user_picture && <AvatarImage src={user_picture} alt={user_name} />}
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
              <span className="truncate text-sm">{user_name}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'visibility',
        header: 'Visibility',
        enableSorting: false,
        cell: ({ row }) => {
          const vis = row.original.visibility;
          if (!vis) return <span className="text-muted-foreground">&mdash;</span>;
          return (
            <span className="text-muted-foreground flex items-center gap-1 text-sm">
              {vis === 'public' ? (
                <>
                  <GlobeIcon className="size-3.5" /> Public
                </>
              ) : (
                <>
                  <LockIcon className="size-3.5" /> Private
                </>
              )}
            </span>
          );
        },
      },
      {
        accessorKey: 'source',
        header: 'Source',
        enableSorting: false,
        cell: ({ row }) => {
          const source = row.original.source;
          if (source === 'gaia_ui') {
            return (
              <span className="text-muted-foreground flex items-center gap-1 text-sm">
                <GlobeIcon className="size-3.5" /> Web
              </span>
            );
          }
          if (source === 'slack') {
            return (
              <span className="text-muted-foreground flex items-center gap-1 text-sm">
                <HashIcon className="size-3.5" /> Slack
              </span>
            );
          }
          if (source === 'copilot') {
            return (
              <span className="text-muted-foreground flex items-center gap-1 text-sm">
                <MessageSquareIcon className="size-3.5" /> Copilot
              </span>
            );
          }
          if (source === 'scheduler') {
            return (
              <span className="text-muted-foreground flex items-center gap-1 text-sm">
                <CalendarClockIcon className="size-3.5" /> Scheduler
              </span>
            );
          }
          return <span className="text-muted-foreground">&mdash;</span>;
        },
      },
    ];

    cols.push({
      accessorKey: 'updated_at',
      header: 'Last edited',
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {formatRelativeDate(row.original.updated_at)}
        </span>
      ),
    });

    if (!compact) {
      cols.push({
        accessorKey: 'created_at',
        header: 'Created',
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {formatRelativeDate(row.original.created_at)}
          </span>
        ),
      });
    }

    cols.push({
      id: 'actions',
      cell: ({ row }) => {
        const isOwner = row.original.user_email === currentUserEmail;
        const canEdit = isAdmin || isOwner;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-8 focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=open]:bg-[var(--axiom-surface-accent)] data-[state=open]:text-[var(--axiom-content-accent)]"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontalIcon className="size-4" />
                <span className="sr-only">Actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {pathPrefix && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    const url = `${window.location.origin}${pathPrefix}/thread/${row.original.thread_id}`;
                    navigator.clipboard.writeText(url);
                    toast.success('Link copied to clipboard');
                  }}
                >
                  <LinkIcon className="size-4" />
                  Copy link
                </DropdownMenuItem>
              )}
              {onCopyThreadId && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onCopyThreadId(row.original.thread_id);
                  }}
                >
                  <ClipboardCopy className="size-4" />
                  Copy Thread ID
                </DropdownMenuItem>
              )}
              {canEdit && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRename(row.original.thread_id);
                  }}
                >
                  <PencilIcon className="size-4" />
                  Rename
                </DropdownMenuItem>
              )}
              {canEdit && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleVisibility(
                      row.original.thread_id,
                      row.original.visibility,
                    );
                  }}
                >
                  {row.original.visibility === 'public' ? (
                    <>
                      <LockIcon className="size-4" />
                      Make private
                    </>
                  ) : (
                    <>
                      <GlobeIcon className="size-4" />
                      Make public
                    </>
                  )}
                </DropdownMenuItem>
              )}
              {canEdit && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(row.original.thread_id);
                    }}
                  >
                    <TrashIcon className="size-4" />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    });

    return cols;
  }, [
    compact,
    currentUserEmail,
    isAdmin,
    pathPrefix,
    onCopyThreadId,
    handleDelete,
    handleRename,
    handleRenameSave,
    handleToggleVisibility,
    renamingThreadId,
  ]);

  const table = useReactTable({
    data: threads,
    columns,
    state: { sorting, pagination, globalFilter },
    onSortingChange: (updater) => {
      setSorting(updater);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    manualPagination: true,
    manualSorting: true,
    pageCount,
    globalFilterFn: (row, _columnId, filterValue: string) => {
      const search = filterValue.toLowerCase();
      const title = (row.original.title ?? '').toLowerCase();
      const userName = (row.original.user_name ?? '').toLowerCase();
      return title.includes(search) || userName.includes(search);
    },
    getRowId: (row) => row.thread_id,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 p-4 lg:p-6">
        <div className="space-y-3">
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (
    totalCount === 0 &&
    !statusFilter &&
    Object.keys(metadataFilters).every((k) => !metadataFilters[k as keyof MetadataFilters])
  ) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-muted-foreground">
        <MessageSquareIcon className="size-12 opacity-30" />
        <p className="text-sm">No conversations yet. Start one!</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 overflow-y-auto p-4 lg:p-6">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <SearchIcon className="text-muted-foreground absolute left-2.5 top-1/2 size-4 -translate-y-1/2" />
          <Input
            placeholder="Search conversations..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="h-8 pl-8"
          />
        </div>
        <Select
          value={metadataFilters.source ?? 'all'}
          onValueChange={(value) => handleMetadataFilterChange('source', value)}
        >
          <SelectTrigger className="h-8 w-[130px]">
            <SelectValue placeholder="All sources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="gaia_ui">Web</SelectItem>
            <SelectItem value="slack">Slack</SelectItem>
            <SelectItem value="copilot">Copilot</SelectItem>
            <SelectItem value="scheduler">Scheduler</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={statusFilter ?? 'all'}
          onValueChange={(value) => {
            setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            setStatusFilter(value === 'all' ? undefined : (value as ThreadStatus));
          }}
        >
          <SelectTrigger className="h-8 w-[140px]">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="idle">Completed</SelectItem>
            <SelectItem value="busy">Running</SelectItem>
            <SelectItem value="interrupted">Interrupted</SelectItem>
            <SelectItem value="error">Error</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={metadataFilters.visibility ?? 'all'}
          onValueChange={(value) => handleMetadataFilterChange('visibility', value)}
        >
          <SelectTrigger className="h-8 w-[150px]">
            <SelectValue placeholder="All visibility" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All visibility</SelectItem>
            <SelectItem value="public">Public</SelectItem>
            <SelectItem value="private">Private</SelectItem>
          </SelectContent>
        </Select>
        {isAdmin && (
          <Select
            value={metadataFilters.user_name ?? 'all'}
            onValueChange={(value) => handleMetadataFilterChange('user_name', value)}
          >
            <SelectTrigger className="h-8 w-[150px]">
              <SelectValue placeholder="All users" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All users</SelectItem>
              {sortedUserNames.map((name) => (
                <SelectItem key={name} value={name}>
                  {name === currentUserName ? `${name} (me)` : name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      <div className="relative overflow-hidden rounded-lg border">
        {isFetching && (
          <div className="absolute inset-x-0 top-0 z-20 h-0.5 overflow-hidden bg-muted">
            <div className="h-full w-1/3 animate-[slide_1s_ease-in-out_infinite] bg-primary" />
          </div>
        )}
        <Table>
          <TableHeader className="bg-muted sticky top-0 z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {header.isPlaceholder ? null : (
                      <span className="flex items-center gap-1">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getIsSorted() === 'asc' && ' \u2191'}
                        {header.column.getIsSorted() === 'desc' && ' \u2193'}
                      </span>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="cursor-pointer"
                  onClick={() => onRowClick(row.original.thread_id)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between px-4">
        <div className="text-muted-foreground hidden flex-1 text-sm lg:flex">
          {totalCount} conversation(s) total
        </div>
        <div className="flex w-full items-center gap-8 lg:w-fit">
          {!compact && (
            <div className="hidden items-center gap-2 lg:flex">
              <span className="text-sm font-medium">Rows per page</span>
              <Select
                value={`${pagination.pageSize}`}
                onValueChange={(value) =>
                  setPagination({ pageIndex: 0, pageSize: Number(value) })
                }
              >
                <SelectTrigger className="h-8 w-20">
                  <SelectValue placeholder={pagination.pageSize} />
                </SelectTrigger>
                <SelectContent side="top">
                  {[10, 20, 30, 50].map((pageSize) => (
                    <SelectItem key={pageSize} value={`${pageSize}`}>
                      {pageSize}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex w-fit items-center justify-center text-sm font-medium">
            Page {pagination.pageIndex + 1} of {pageCount || 1}
          </div>
          <div className="ml-auto flex items-center gap-2 lg:ml-0">
            <Button
              variant="outline"
              className="hidden size-8 lg:flex"
              size="icon"
              onClick={() => setPagination((prev) => ({ ...prev, pageIndex: 0 }))}
              disabled={pagination.pageIndex === 0}
            >
              <span className="sr-only">Go to first page</span>
              <ChevronsLeftIcon />
            </Button>
            <Button
              variant="outline"
              className="size-8"
              size="icon"
              onClick={() =>
                setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex - 1 }))
              }
              disabled={pagination.pageIndex === 0}
            >
              <span className="sr-only">Go to previous page</span>
              <ChevronLeftIcon />
            </Button>
            <Button
              variant="outline"
              className="size-8"
              size="icon"
              onClick={() =>
                setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex + 1 }))
              }
              disabled={pagination.pageIndex >= pageCount - 1}
            >
              <span className="sr-only">Go to next page</span>
              <ChevronRightIcon />
            </Button>
            <Button
              variant="outline"
              className="hidden size-8 lg:flex"
              size="icon"
              onClick={() =>
                setPagination((prev) => ({ ...prev, pageIndex: pageCount - 1 }))
              }
              disabled={pagination.pageIndex >= pageCount - 1}
            >
              <span className="sr-only">Go to last page</span>
              <ChevronsRightIcon />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InlineRenameInput({
  defaultValue,
  onSave,
  onCancel,
}: {
  defaultValue: string;
  onSave: (value: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(defaultValue);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.select();
  }, []);

  const handleSave = () => {
    const trimmed = value.trim();
    if (trimmed) onSave(trimmed);
    else onCancel();
  };

  return (
    <input
      ref={ref}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleSave();
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          onCancel();
        }
        e.stopPropagation();
      }}
      onBlur={handleSave}
      onClick={(e) => e.stopPropagation()}
      className="w-full bg-transparent font-medium outline-none ring-1 ring-ring rounded px-1"
      autoFocus
    />
  );
}
